import FilePreviewPopover from "components/FilePreview/FilePreviewPopover";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import LoadingSpinner from "components/LoaderErrorWrapper/LoadingSpinner";
import PageContainer from "components/PageContainer/PageContainer";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";
import Colors from "Constants/Color";
import { tableStyles } from "Constants/Style";
import React, { useEffect } from "react";
import { Button, Card, Col, Row, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import { fetchEnquiryById } from "slices/enquiry/thunk";
import CancelBackButton from "components/Buttons/CancelBackButton";
import StatusChip from "components/Chips/StatusChip";

const EnquiryDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  // Redux selector for enquiry state
  const selectEnquiryData = createSelector(
    (state: any) => state.Enquiry,
    (enquiry) => ({
      selectedEnquiry: enquiry.selectedEnquiry,
      loading: enquiry.loading,
      error: enquiry.error,
    })
  );

  const { selectedEnquiry, loading, error } = useSelector(selectEnquiryData);

  // Fetch enquiry data using Redux thunk
  useEffect(() => {
    if (!id) {
      toast.error("Enquiry ID is missing");
      navigate("/enquiry");
      return;
    }

    // Dispatch thunk to fetch enquiry by ID
    dispatch(fetchEnquiryById(id));
  }, [id, dispatch, navigate]);

  const handleCancel = () => {
    navigate("/enquiry");
  };

  const formatDate = (date: string | null | undefined) => {
    if (!date) return "—";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };
  const enquiryData = selectedEnquiry;

  return (
    <PageContainer>
      <Card.Header>
        <FormHeader
          title="View Enquiry"
          firstLabel="Enquiry"
          secondLabel="Enquiry details"
          handleArrowClick={handleCancel}
        />
      </Card.Header>

      <hr />

      {loading ? (
        <LoadingSpinner />
      ) : !selectedEnquiry ? (
        <div className="text-center py-5 bg-white rounded-3 shadow-sm">
          <p>No enquiry data found</p>
          <Button variant="primary" onClick={handleCancel}>
            Back to Enquiry List
          </Button>
        </div>
      ) : (
        <Card.Body>
          {/* Enquiry Details */}
          <Row className="mb-3">
            {/* <p>
              <h6 className="card-title mb-0 mt-3">Enquiry Details</h6>
            </p> */}
            <Col md={6}>
              <p>
                <strong>Enquiry No. :</strong>&nbsp;
                {enquiryData?.enquiry_number || "—"}
              </p>
            </Col>
            <Col md={6}>
              <p>
                <strong>Enquiry Date. :</strong>&nbsp;
                {formatDate(enquiryData?.enquiry_date)}
              </p>
            </Col>
            <Col md={6}>
              <p>
                <strong>Source of Enquiry :</strong>&nbsp;
                {enquiryData?.enquiry_source || "—"}
              </p>
            </Col>
            <Col md={6}>
              <p>
                <strong>Initiated By :</strong>&nbsp;
                {enquiryData?.created_by_user?.first_name +
                  " " +
                  enquiryData?.created_by_user?.last_name ||
                  enquiryData?.created_by_user?.username ||
                  enquiryData?.created_by_user?.email ||
                  enquiryData?.created_by ||
                  "—"}
              </p>
            </Col>

            <Col md={6}>
              <p>
                <strong>Stage :</strong>&nbsp;
                <StatusChip status={enquiryData?.stage as any}>
                  {enquiryData?.stage as any}
                </StatusChip>
              </p>
            </Col>

            <Col md={6}>
              <p>
                <strong>Remark :</strong>&nbsp;
                {enquiryData?.remark || "—"}
              </p>
            </Col>
          </Row>

          <hr />

          {/* Customer Details */}
          <Row className="mb-3">
            <p>
              <h6 className="card-title mb-0 mt-3">Customer Details</h6>
            </p>
            <div
              style={{
                backgroundColor: `${Colors?.background_color}`,
                padding: "0 20px",
                borderRadius: "8px",
                border: `1px solid ${Colors?.background_color}`,
              }}
            >
              <Row className="mb-0 mt-3">
                <Col md={6}>
                  <p>
                    <strong>Customer Name :</strong>&nbsp;
                    {enquiryData?.customer_details?.customer_name || "—"}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Customer Type :</strong>&nbsp;
                    {enquiryData?.customer_details?.customer_type || "—"}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>GSTIN :</strong>&nbsp;
                    {enquiryData?.customer_details?.gstin || "—"}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Email :</strong>&nbsp;
                    {enquiryData?.customer_details?.email || "—"}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Phone Number :</strong>&nbsp;
                    {enquiryData?.customer_details?.contact_number ||
                      enquiryData?.customer_details?.phone_number ||
                      "—"}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Aadhar No. :</strong>&nbsp;
                    {enquiryData?.customer_details?.aadhar_no
                      ? `xxxx xxxx ${enquiryData.customer_details.aadhar_no.slice(
                          -2
                        )}`
                      : "—"}
                  </p>
                </Col>
                <Col md={12}>
                  <p>
                    <strong>Address :</strong>&nbsp;
                    {[
                      enquiryData?.customer_details?.address,
                      enquiryData?.customer_details?.city,
                      enquiryData?.customer_details?.pincode,
                    ]
                      .filter(Boolean)
                      .join(", ") || "—"}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Contact Person Name :</strong>&nbsp;
                    {enquiryData?.customer_details?.contact_person_name || "—"}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Contact Person No. :</strong>&nbsp;
                    {enquiryData?.customer_details?.contact_person_phone_no ||
                      enquiryData?.customer_details?.contact_person_mobile_no ||
                      "—"}
                  </p>
                </Col>
              </Row>
            </div>
          </Row>

          <hr />

          {/* Site Visit Details */}
          {enquiryData?.site_visit_required && enquiryData?.site_visit && (
            <>
              <Row className="mb-3">
                <p>
                  <h6 className="card-title mb-0 mt-3">Site Visit Details</h6>
                </p>
                <Col md={6}>
                  <p>
                    <strong>Site Visit Date :</strong>&nbsp;
                    {formatDate(enquiryData?.site_visit?.site_visit_date)}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Staff Name :</strong>&nbsp;
                    {enquiryData?.site_visit?.staff_name || "—"}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Phone Number :</strong>&nbsp;
                    {enquiryData?.site_visit?.phone_number ||
                      enquiryData?.site_visit?.staff_phone ||
                      "—"}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Address :</strong>&nbsp;
                    {enquiryData?.site_visit?.address || "—"}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>City :</strong>&nbsp;
                    {enquiryData?.site_visit?.city || "—"}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>State :</strong>&nbsp;
                    {enquiryData?.site_visit?.state || "—"}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Location Url :</strong>&nbsp;
                    {enquiryData?.site_visit?.location_url ? (
                      <a
                        href={enquiryData.site_visit.location_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          textDecoration: "underline",
                          color: "#0033aa", // dark blue
                          fontFamily: "Poppins, sans-serif",
                          fontSize: "13px",
                        }}
                      >
                        {enquiryData.site_visit.location_url
                          ? "Visit location"
                          : "-"}
                      </a>
                    ) : (
                      "—"
                    )}
                  </p>
                </Col>
                <Col md={12}>
                  <p>
                    <strong>Remark :</strong>&nbsp;
                    {enquiryData?.site_visit?.remarks || "—"}
                  </p>
                </Col>
              </Row>
              <hr />
            </>
          )}

          {/* Item Details */}
          {enquiryData?.enquiry_items &&
            enquiryData.enquiry_items.length > 0 && (
              <>
                <Row className="mb-3">
                  <p>
                    <h6 className="card-title mb-0 mt-3">Item Details</h6>
                  </p>
                  <Col md={12}>
                    <style>
                      {`
                          .sticky-column {
                            position: relative;
                          }
                          .sticky-column::before {
                            content: '';
                            position: absolute;
                            left: 0;
                            top: 0;
                            bottom: 0;
                            width: 1px;
                            background-color: #dee2e6;
                            z-index: 12;
                          }
                          thead .sticky-column::before {
                            z-index: 13;
                          }
                          .remark-cell {
                            max-width: 300px;
                            white-space: normal !important;
                            word-wrap: break-word;
                            overflow-wrap: break-word;
                          }
                        `}
                    </style>
                    <div className="mb-3 mt-3" style={{ overflowX: "auto" }}>
                      <Table
                        hover
                        responsive
                        className="align-middle text-center"
                      >
                        <thead>
                          <tr>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "70px",
                                padding: "12px",
                              }}
                            >
                              Sr No.
                            </th>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "150px",
                                padding: "12px",
                              }}
                            >
                              Item Name
                            </th>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "120px",
                                padding: "12px",
                              }}
                            >
                              Item Code
                            </th>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "150px",
                                padding: "12px",
                              }}
                            >
                              Category
                            </th>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "150px",
                                padding: "12px",
                              }}
                            >
                              Sub Category
                            </th>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "100px",
                                padding: "12px",
                              }}
                            >
                              Unit
                            </th>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "100px",
                                padding: "12px",
                              }}
                            >
                              Length
                            </th>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "100px",
                                padding: "12px",
                              }}
                            >
                              Width
                            </th>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "100px",
                                padding: "12px",
                              }}
                            >
                              Height
                            </th>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "180px",
                                padding: "12px",
                              }}
                            >
                              Required Quantity
                            </th>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "100px",
                                padding: "12px",
                              }}
                            >
                              Document
                            </th>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "100px",
                                padding: "12px",
                              }}
                            >
                              Remarks
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {enquiryData.enquiry_items.map(
                            (item: any, index: number) => (
                              <tr
                                key={item.enquiry_item_id || index}
                                style={{
                                  backgroundColor:
                                    index % 2 === 0 ? "#ffffff" : "#e3f2fd",
                                }}
                              >
                                <td style={{ padding: "12px" }}>{index + 1}</td>
                                <td style={{ padding: "12px" }}>
                                  {item.item_name || "—"}
                                </td>
                                <td style={{ padding: "12px" }}>
                                  {item.item_code || "—"}
                                </td>
                                <td style={{ padding: "12px" }}>
                                  {item.category_name || "—"}
                                </td>
                                <td style={{ padding: "12px" }}>
                                  {item.sub_category_name || "—"}
                                </td>
                                <td style={{ padding: "12px" }}>
                                  {item.unit?.unit_name ||
                                    item.unit_name ||
                                    "—"}
                                </td>
                                <td style={{ padding: "12px" }}>
                                  {item.length || "—"}
                                </td>
                                <td style={{ padding: "12px" }}>
                                  {item.width || "—"}
                                </td>
                                <td style={{ padding: "12px" }}>
                                  {item.height || "—"}
                                </td>
                                <td style={{ padding: "12px" }}>
                                  {item?.required_quantity || "—"}
                                </td>
                                <td style={{ padding: "12px" }}>
                                  {item?.item_file ? (
                                    <FilePreviewPopover
                                      fileUrl={item?.item_file}
                                      index={index}
                                      displayText={"Attachment"}
                                    />
                                  ) : (
                                    "—"
                                  )}
                                </td>
                                <td className="remark-cell" style={{ padding: "12px" }}>
                                  <TruncatedTextCell
                                    text={item?.remarks}
                                    maxWidth="300px"
                                    maxLines={4}
                                    rowIndex={index}
                                    fallbackText="—"
                                  />
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Col>
                </Row>
                <hr />
              </>
            )}
          <div className="d-flex flex-wrap justify-content-center gap-3 mt-5">
            <CancelBackButton to="/enquiry" label="Back" />
          </div>
        </Card.Body>
      )}
    </PageContainer>
  );
};

export default EnquiryDetailsPage;
